import {
  FileUpload as ChakraFileUpload,
  FileUploadItemPreviewImage,
  FileUploadRootProvider,
  Image,
  useFileUpload,
  UseFileUploadReturn,
} from '@chakra-ui/react';
import React, { createContext, useContext, useState } from 'react';

import Button from 'ui/Button';
import Input, { InputProps } from 'ui/Input';
import { Box, Float, HStack, StackProps, VStack } from 'ui/Layout';
import { Text } from 'ui/Typography';

import { spinAnimation } from '../constants';
import Dialog from './Dialog';
import Icon from './Icon';

const FileUploadContext = createContext<{
  isLimitReached: boolean;
  isProcessing: boolean;
  fileUpload: UseFileUploadReturn | undefined;
}>({
  isLimitReached: false,
  isProcessing: false,
  fileUpload: undefined,
});

const ClipboardInput: React.FC<InputProps> = ({ onPaste, ...props }) => {
  const { isLimitReached, isProcessing, fileUpload } =
    useContext(FileUploadContext);
  return (
    <Input
      {...props}
      readOnly
      disabled={isLimitReached || isProcessing}
      onPaste={(e) => {
        fileUpload?.setClipboardFiles(e.clipboardData);
        onPaste?.(e);
      }}
    />
  );
};

type DropzoneProps = StackProps &
  ChakraFileUpload.DropzoneProps & {
    description: React.ReactNode;
  };

const Dropzone = React.forwardRef<HTMLInputElement, DropzoneProps>(
  ({ description, ...rest }, ref) => {
    const { isLimitReached, isProcessing, fileUpload } =
      useContext(FileUploadContext);
    const hasFiles = !!fileUpload?.acceptedFiles.length;

    const content = (
      <VStack gap={5} justifyContent="center" minH="180px" textAlign="center">
        <Icon
          animation={
            isProcessing ? `${spinAnimation} 1s infinite linear` : undefined
          }
          color="gray.600"
          fontSize="3xl"
          icon={
            isLimitReached
              ? 'file-circle-exclamation'
              : isProcessing
                ? 'spinner'
                : 'cloud-arrow-up'
          }
        />
        <VStack gap={1}>
          <Text color="gray.700">
            {isLimitReached
              ? "You've reached the file limit"
              : isProcessing
                ? 'Parsing uploaded file...'
                : 'Drag and drop here or click to upload'}
          </Text>
          <Text color="gray.500" fontSize="sm">
            {description}
          </Text>
        </VStack>
        {hasFiles && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              fileUpload?.clearFiles();
            }}
            disabled={isProcessing}
            variant="danger-ghost"
            size="sm"
          >
            <Icon icon="trash-can-regular" /> Clear uploads
          </Button>
        )}
      </VStack>
    );

    return isLimitReached || isProcessing ? (
      <Box
        background="gray.50"
        border="1px solid"
        borderColor="gray.400"
        cursor="not-allowed"
        justifyContent="center"
        p={2}
        my="1px"
        {...rest}
      >
        {content}
      </Box>
    ) : (
      <ChakraFileUpload.Dropzone
        background="white"
        border="2px dashed"
        borderColor="gray.300"
        cursor="pointer"
        focusRing="mixed"
        focusRingColor="blue.400"
        focusRingWidth="0"
        minH={0}
        p={2}
        ref={ref}
        css={{
          '&[data-dragging]': {
            background: 'blue.50',
            borderColor: 'blue.300',
          },
        }}
        {...rest}
      >
        {content}
      </ChakraFileUpload.Dropzone>
    );
  }
);

type ImagePreviewProps = {
  boxSize: number;
  disabled?: boolean;
  imageFile: File;
};

const ImagePreview: React.FC<ImagePreviewProps> = ({
  boxSize,
  disabled,
  imageFile,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <ChakraFileUpload.Item
      key={imageFile.name}
      file={imageFile}
      p={1}
      position="relative"
      w="auto"
    >
      <Float placement="top-end">
        <ChakraFileUpload.ItemDeleteTrigger asChild>
          <Button disabled={disabled} variant="danger-ghost" size="xs">
            <Icon icon="trash-can-regular" />
          </Button>
        </ChakraFileUpload.ItemDeleteTrigger>
      </Float>
      <FileUploadItemPreviewImage
        boxSize={boxSize}
        cursor={disabled ? 'not-allowed' : 'zoom-in'}
        objectFit="cover"
        onClick={() => !disabled && setOpen(true)}
      />
      <Dialog.Root
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
        lazyMount
        unmountOnExit
      >
        {/* Show image preview above its current dialog */}
        <Dialog.Content zIndex="popover">
          <Dialog.Body onClick={() => setOpen(false)}>
            <Image src={URL.createObjectURL(imageFile)} />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Root>
    </ChakraFileUpload.Item>
  );
};

type ImagePreviewsProps = {
  imageFiles: File[];
};

const ImagePreviews: React.FC<ImagePreviewsProps> = ({ imageFiles }) => {
  const { isProcessing } = useContext(FileUploadContext);

  // Render nothing if no images
  const count = imageFiles.length;
  if (count === 0) {
    return null;
  }

  // Adjust image preview size based on the number of images to fit it nicely in the layout
  const boxSize = count <= 6 ? 20 : count <= 32 ? 14 : 7;

  return (
    <HStack gap={4} my={2} wrap="wrap">
      {imageFiles.map((imageFile) => (
        <ImagePreview
          key={imageFile.name}
          boxSize={boxSize}
          disabled={isProcessing}
          imageFile={imageFile}
        />
      ))}
    </HStack>
  );
};

type RootProps = {
  children: React.ReactNode;
  fileUpload: UseFileUploadReturn;
  isLimitReached?: boolean;
  isProcessing?: boolean;
};

const Root: React.FC<RootProps> = ({
  children,
  fileUpload,
  isLimitReached = false,
  isProcessing = false,
}) => (
  <FileUploadContext.Provider
    value={{ isLimitReached, isProcessing, fileUpload }}
  >
    <FileUploadRootProvider alignItems="stretch" gap={3} value={fileUpload}>
      <ChakraFileUpload.HiddenInput />
      {children}
    </FileUploadRootProvider>
  </FileUploadContext.Provider>
);

const FileUpload = {
  ClipboardInput,
  Dropzone,
  ImagePreviews,
  Root,
};

export default FileUpload;

export { useFileUpload };
export type { UseFileUploadReturn };
